import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put, del } from '../utils/api';
import { notify } from '../utils/alertBridge';
import { getApiErrorMessage, getApiSuccessMessage } from '../utils/apiMessage';

/**
 * Enhanced API operations hook with standardized error/success alerting.
 *
 * Mutations automatically surface a global error alert on failure (opt-out) and,
 * when the caller supplies copy, a success alert. Both use the app-wide
 * GlobalAlert Snackbar via the alert bridge — no need to wire setAlert in every
 * component. See resolveAlertOptions for the per-call options.
 */

// Alert-behaviour options recognised by the mutation hooks. Extracted so they're
// never spread onto react-query's config (which would warn on unknown keys).
//   showErrorAlert   {boolean}  default true  — auto-show an error Snackbar on failure
//   errorMessage     {string|fn} override the error copy (fn receives the error)
//   showSuccessAlert {boolean}  default false — auto-show a success Snackbar
//   successMessage   {string|fn} success copy (fn receives the response data);
//                                 supplying this implies showSuccessAlert: true
const ALERT_OPTION_KEYS = ['showErrorAlert', 'errorMessage', 'showSuccessAlert', 'successMessage'];

const resolveAlertOptions = (options = {}) => {
  const {
    showErrorAlert = true,
    errorMessage,
    showSuccessAlert = false,
    successMessage,
  } = options;
  return {
    showErrorAlert,
    errorMessage,
    // A provided successMessage implies the caller wants a success alert.
    showSuccessAlert: showSuccessAlert || successMessage != null,
    successMessage,
  };
};

// Strip alert keys from the options before spreading the rest onto react-query.
const stripAlertOptions = (options = {}) => {
  const rest = { ...options };
  ALERT_OPTION_KEYS.forEach((key) => delete rest[key]);
  return rest;
};

// Fire the error alert for a failed mutation (unless opted out). `errorMessage`
// may be a string or a (error) => string function; otherwise we derive a clean
// message from the thrown API error.
const alertError = (error, alertOptions) => {
  if (!alertOptions.showErrorAlert) return;
  const override = typeof alertOptions.errorMessage === 'function'
    ? alertOptions.errorMessage(error)
    : alertOptions.errorMessage;
  notify(getApiErrorMessage(error, override), 'error');
};

// Fire the success alert (only when the caller opted in / supplied copy).
const alertSuccess = (data, alertOptions) => {
  if (!alertOptions.showSuccessAlert) return;
  const explicit = typeof alertOptions.successMessage === 'function'
    ? alertOptions.successMessage(data)
    : alertOptions.successMessage;
  const message = getApiSuccessMessage(data, explicit);
  if (message) notify(message, 'success');
};

/**
 * Generic query hook with standardized options. Queries stay silent by design —
 * background refetches/retries shouldn't spam toasts. Handle query errors in the
 * component (isError) or pass your own onError.
 */
export const useApiQuery = (queryKey, queryFn, options = {}) => {
  return useQuery({
    queryKey,
    queryFn,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes (v5 renamed cacheTime -> gcTime)
    retry: 0,
    ...options,
  });
};

/**
 * Generic mutation hook with standardized error/success alerting.
 *
 * By default: shows an error Snackbar on failure; success is silent unless you
 * pass successMessage / showSuccessAlert. The caller's own onError/onSuccess
 * still run (composed, not overridden). Opt out of the error alert with
 * `showErrorAlert: false`.
 */
export const useApiMutation = (mutationFn, options = {}) => {
  const alertOptions = resolveAlertOptions(options);
  const rest = stripAlertOptions(options);

  return useMutation({
    mutationFn,
    ...rest,
    // Defined AFTER the spread so the hook's alerting always runs; the caller's
    // callbacks are invoked explicitly from within, so nothing is lost.
    onError: (error, variables, context) => {
      alertError(error, alertOptions);
      options.onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      alertSuccess(data, alertOptions);
      options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * CRUD operations hook for resource management. Each operation gets a default
 * error alert; supply `create/update/removeSuccessMessage` to also toast on
 * success. Set `showErrorAlert: false` to silence errors for the whole resource.
 */
export const useCrudOperations = (resourceName, baseUrl, additionalInvalidateKeys = [], options = {}) => {
  const queryClient = useQueryClient();
  const showErrorAlert = options.showErrorAlert !== false;

  // Helper to get standard invalidate keys for a resource
  const getInvalidateKeys = () => {
    const baseKeys = [[resourceName]];
    return [...baseKeys, ...additionalInvalidateKeys];
  };

  const invalidate = () => {
    getInvalidateKeys().forEach((key) => {
      queryClient.invalidateQueries({ queryKey: key });
    });
  };

  const onError = (verb) => (error) => {
    if (showErrorAlert) {
      notify(getApiErrorMessage(error, `Failed to ${verb} ${resourceName}.`), 'error');
    }
  };

  const create = useMutation({
    mutationFn: async (data) => {
      return await post(baseUrl, data);
    },
    onSuccess: (data, variables, context) => {
      invalidate();
      if (options?.createSuccessMessage) notify(options.createSuccessMessage, 'success');
      if (options?.onCreateSuccess) {
        options.onCreateSuccess(data, variables, context);
      }
    },
    onError: onError('create'),
  });

  const update = useMutation({
    mutationFn: async ({ id, data }) => {
      return await put(`${baseUrl}/${id}`, data);
    },
    onSuccess: (data, variables, context) => {
      invalidate();
      if (options?.updateSuccessMessage) notify(options.updateSuccessMessage, 'success');
      if (options?.onUpdateSuccess) {
        options.onUpdateSuccess(data, variables, context);
      }
    },
    onError: onError('update'),
  });

  const remove = useMutation({
    mutationFn: async (id) => {
      return await del(`${baseUrl}/${id}`);
    },
    onSuccess: (data, variables, context) => {
      invalidate();
      if (options?.removeSuccessMessage) notify(options.removeSuccessMessage, 'success');
      if (options?.onRemoveSuccess) {
        options.onRemoveSuccess(data, variables, context);
      }
    },
    onError: onError('delete'),
  });

  return { create, update, remove };
};
